'use client';
import EmojiIconSvg from '@/assets/svgs/text-editor/emoji.svg';
import { selectExtraStateItems } from '@/lib/redux/app.selectors';
import { setExtraStateItems } from '@/lib/redux/app.slice';
import { useAppDispatch } from '@/lib/redux/store';
import { getShortcutModifier, isMac } from '@/shared/utils/window';
import { Editor } from '@tiptap/react';
import classNames from 'classnames';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useTextEditor from '../hooks/useTextEditor';
import useTextEditorShortcuts from '../hooks/useTextEditorShortcuts';
import KeyboardShortcutText from './KeyboardShortcutText';

interface EmojiPickerButtonProps {
  className?: string;
  editor: Editor | null;
}

const EmojiPickerButton = ({ className, editor }: EmojiPickerButtonProps) => {
  const { closeFloatingItems } = useTextEditorShortcuts();
  const dispatch = useAppDispatch();
  const extraItems = useSelector(selectExtraStateItems);
  const { skinTone } = extraItems ?? {};
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const [isClickOpen, setIsClickOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { replaceSelectedText } = useTextEditor(editor);

  const handleHoverEnter = () => {
    setIsHoverOpen(true);
    setIsClickOpen(false); // Close click dropdown when hover opens
  };

  const handleHoverLeave = () => {
    setIsHoverOpen(false);
  };

  const handleClickToggle = () => {
    closeFloatingItems();
    setIsClickOpen((prev) => !prev);
    setIsHoverOpen(false); // Close hover dropdown when click toggles
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isClickOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsClickOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isClickOpen]);

  return (
    <div
      ref={dropdownRef}
      className={classNames(
        'dropdown',
        {
          'dropdown-top dropdown-hover': isHoverOpen && !isClickOpen,
          'dropdown-top dropdown-open': isClickOpen,
        },
        className
      )}
      data-dropdown-dismiss="true">
      <div
        tabIndex={0}
        role="button"
        onMouseDown={(e) => {
          e.preventDefault(); // Prevent button from stealing focus from editor
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleClickToggle();
        }}
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}
        className={classNames('btn btn-square btn-ghost btn-sm')}>
        <EmojiIconSvg />
      </div>
      {isHoverOpen && !isClickOpen && (
        <div
          tabIndex={0}
          className="dropdown-content bg-black-default/80 z-[1] mb-0.5 !flex h-[26px] min-w-[143px] items-center justify-center gap-1.5 rounded-md px-[8px] text-white">
          <div className="text-[12px]">Add emoji</div>
          <div className="shortcuts flex gap-1">
            <KeyboardShortcutText
              shortcut={getShortcutModifier()}
              className="text-[16px]"
            />
            <KeyboardShortcutText
              shortcut="⇧"
              className={isMac() ? 'pb-0.5 text-[16px]' : 'text-[12px]'}
            />
            <KeyboardShortcutText shortcut="E" className="text-[12px]" />
          </div>
        </div>
      )}

      {isClickOpen && (
        <div className="dropdown-content relative">
          <div className="absolute">
            <EmojiPicker
              theme={Theme.AUTO}
              onEmojiClick={(emoji) => {
                replaceSelectedText(emoji.emoji);
                setIsHoverOpen(false);
                setIsClickOpen(false); // Close after emoji selection
              }}
              defaultSkinTone={skinTone}
              onSkinToneChange={(skinTone) => {
                dispatch(
                  setExtraStateItems({
                    skinTone,
                  })
                );
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EmojiPickerButton;
